import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../hooks/useAuth';
import { getSchool, updateSchool, School } from '../../services/schoolService';
import { formatPhoneNumber, unformatPhoneNumber } from '../../utils/phoneFormatter';

/**
 * SchoolSettings component for viewing and editing school information
 * Only accessible by school_admin users
 */
const SchoolSettings: React.FC = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState<School | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    school_slug: '',
    address: '',
    phone: '',
    email: '',
    website: '',
  });

  // Form errors
  const [formErrors, setFormErrors] = useState({
    name: '',
    school_slug: '',
    email: '',
    website: '',
  });

  // Get school_id from user attributes
  const schoolId = user?.attributes?.['custom:school_id'];

  useEffect(() => {
    if (schoolId) {
      loadSchool();
    }
  }, [schoolId]);

  const loadSchool = async () => {
    try {
      setLoading(true);
      setError(null);
      const schoolData = await getSchool(schoolId);
      setSchool(schoolData);

      // Populate form data with formatted phone
      const rawPhone = schoolData.contact_info?.phone || '';
      setFormData({
        name: schoolData.name || '',
        school_slug: schoolData.school_slug || '',
        address: schoolData.address || '',
        phone: rawPhone ? formatPhoneNumber(rawPhone) : '',
        email: schoolData.contact_info?.email || '',
        website: schoolData.contact_info?.website || '',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load school information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Special handling for phone number - format as user types
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    setFormErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateForm = (): boolean => {
    const errors = {
      name: '',
      school_slug: '',
      email: '',
      website: '',
    };
    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'School name is required';
      isValid = false;
    } else if (formData.name.trim().length < 3) {
      errors.name = 'School name must be at least 3 characters';
      isValid = false;
    }

    // Validate school slug
    if (!formData.school_slug.trim()) {
      errors.school_slug = 'School slug is required';
      isValid = false;
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(formData.school_slug)) {
      errors.school_slug = 'School slug must contain only lowercase letters, numbers, and hyphens';
      isValid = false;
    }

    // Validate email if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
      isValid = false;
    }

    // Validate website if provided
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      errors.website = 'Website must start with http:// or https://';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);

    // Reset form data to original school data with formatted phone
    if (school) {
      const rawPhone = school.contact_info?.phone || '';
      setFormData({
        name: school.name || '',
        school_slug: school.school_slug || '',
        address: school.address || '',
        phone: rawPhone ? formatPhoneNumber(rawPhone) : '',
        email: school.contact_info?.email || '',
        website: school.contact_info?.website || '',
      });
    }

    // Clear form errors
    setFormErrors({
      name: '',
      school_slug: '',
      email: '',
      website: '',
    });
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Unformat phone number before saving (store as xxxyyyzzzz)
      const rawPhone = unformatPhoneNumber(formData.phone);

      const updates = {
        name: formData.name,
        address: formData.address || undefined,
        contact_info: {
          phone: rawPhone || undefined,
          email: formData.email || undefined,
          website: formData.website || undefined,
        },
      };

      const response = await updateSchool(schoolId, updates);
      setSchool(response.school);
      setSuccess('School information updated successfully');
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update school information');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!school) {
    return (
      <Box p={3}>
        <Alert severity="error">School information not found</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        School Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">School Information</Typography>
            {!isEditing && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
                Edit
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="School Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!isEditing}
                required
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="School Slug"
                name="school_slug"
                value={formData.school_slug}
                onChange={handleInputChange}
                disabled={true}
                required
                error={!!formErrors.school_slug}
                helperText="School slug cannot be changed after creation"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                disabled={!isEditing}
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="(555) 123-4567"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing}
                error={!!formErrors.email}
                helperText={formErrors.email}
                placeholder="info@school.edu"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                disabled={!isEditing}
                error={!!formErrors.website}
                helperText={formErrors.website}
                placeholder="https://school.edu"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Created: {formatDate(school.created_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last Updated: {formatDate(school.updated_at)}
              </Typography>
            </Grid>

            {isEditing && (
              <Grid item xs={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolSettings;
